import React, { useEffect, useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { StackLayout, HeadingText } from '@leagueplatform/genesis-core';
import defaultProfileImg from 'assets/default-profile-image.svg';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { PageHeader } from 'components/page-header/page-header.component';
import { PageContentWrapper } from 'components/page-content-wrapper.component';
import { EXTERNAL_LINKS } from 'common/links';
import {
  UserProfileEditableForm,
  LoadingSpinner,
  ErrorState,
  EditProfilePhoto,
  useUserProfileSave,
  useFormState,
} from '@telus-alberta-health-web/shared-module';
import { usePersonalInformation } from 'hooks/use-personal-information/use-personal-information.hook';
import {
  SECONDARY_CONTENT_MAX_WIDTH,
  YEAR_DATE_FORMAT_UTC,
} from 'common/constants';
import { GenderIdentityTooltip } from 'components/modals/gender-identity-tooltip.component';
import {
  PRODUCT_AREA,
  SCREEN_NAMES,
  SUB_PRODUCT_AREA,
  usePageViewAnalytics,
} from '@leagueplatform/analytics';

const FORM_CONTENT_MAX_WIDTH = '628px';

export const PersonalInformationPage = () => {
  const { formatMessage, formatDate } = useIntl();
  const [errorState, setErrorState] = useState<Error | any>(null);

  useDocumentTitle(formatMessage({ id: 'STR_PERSONAL_INFO' }));

  usePageViewAnalytics({
    product_area: PRODUCT_AREA.SETTINGS,
    screen_name: SCREEN_NAMES.PERSONAL_INFO,
    sub_product_area: SUB_PRODUCT_AREA.PERSONAL_INFO,
  });

  const {
    data: personalInformation,
    isLoading: isPersonalInfoLoading,
    error: personalInfoError,
  } = usePersonalInformation();

  const { handleUserProfileSave, isLoading: isUserProfileSaveLoading } =
    useUserProfileSave();

  useEffect(() => {
    if (personalInfoError) {
      setErrorState(personalInfoError);
    } else {
      setErrorState(null);
    }
  }, [personalInfoError]);

  const dateOfBirth = personalInformation?.userProfile?.dateOfBirth;
  const email = personalInformation?.email;

  const genderIdentity = personalInformation?.userProfile?.genderIdentity;

  const formFields = [
    {
      id: 'preferredFirstName',
      name: 'preferredFirstName',
      label: formatMessage({ id: 'STR_PREFERRED_NAME' }),
      placeholder: formatMessage({ id: 'STR_ENTER_PREFERRED_NAME' }),
      value: personalInformation?.preferredFirstName,
      editable: true,
    },
    {
      id: 'email',
      name: 'email',
      label: formatMessage({ id: 'STR_EMAIL_ADDRESS' }),
      value: email,
      editable: false,
      required: true,
    },
    {
      id: 'birthYear',
      name: 'birthYear',
      label: formatMessage({ id: 'STR_BIRTH_YEAR' }),
      value: formatDate(dateOfBirth, YEAR_DATE_FORMAT_UTC),
      editable: false,
      required: true,
      type: 'text',
    },
    {
      id: 'genderIdentity',
      name: 'genderIdentity',
      label: formatMessage({ id: 'STR_GENDER_IDENTITY' }),
      placeholder: formatMessage({ id: 'STR_SELECT' }),
      value: genderIdentity,
      editable: true,
      type: 'select',
      options: [
        { value: '', label: formatMessage({ id: 'STR_SELECT' }) },

        {
          value: 'man',
          label: formatMessage({ id: 'STR_MAN' }),
        },
        {
          value: 'woman',
          label: formatMessage({ id: 'STR_WOMAN' }),
        },
        {
          value: 'agender',
          label: formatMessage({ id: 'STR_AGENDER' }),
        },
        {
          value: 'bigender',
          label: formatMessage({ id: 'STR_BIGENDER' }),
        },
        {
          value: 'genderqueer',
          label: formatMessage({ id: 'STR_GENDERQUEER' }),
        },
        {
          value: 'genderqueer-nonconforming',
          label: formatMessage({ id: 'STR_GENDERQUEER_NONCONFORMING' }),
        },
        {
          value: 'intersex',
          label: formatMessage({ id: 'STR_INTERSEX' }),
        },
        {
          value: 'non-binary',
          label: formatMessage({ id: 'STR_NON_BINARY' }),
        },
        {
          value: 'transgender-man',
          label: formatMessage({ id: 'STR_TRANSGENDER_MAN' }),
        },
        {
          value: 'transgender-woman',
          label: formatMessage({ id: 'STR_TRANSGENDER_WOMAN' }),
        },
        {
          value: 'two-spirit',
          label: formatMessage({ id: 'STR_TWO_SPIRIT' }),
        },
      ],
      tooltip: {
        text: formatMessage({ id: 'STR_WHY_DO_YOU_ASK' }),
        type: 'modal',
        component: GenderIdentityTooltip,
      },
    },
  ];

  const formState = useFormState(formFields);

  if (isPersonalInfoLoading) {
    return <LoadingSpinner />;
  }
  if (personalInfoError) {
    return <ErrorState error={personalInfoError} />;
  }

  return (
    <PageContentWrapper css={{ maxWidth: SECONDARY_CONTENT_MAX_WIDTH }}>
      <PageHeader
        headingText={formatMessage({ id: 'STR_PERSONAL_INFO' })}
        showBackButton={false}
      />

      {errorState && <ErrorState error={errorState} />}
      {!errorState && (
        <>
          <StackLayout
            horizontalAlignment="center"
            css={{
              marginBottom: '$twoAndHalf',
            }}
          >
            <EditProfilePhoto
              defaultProfileImg={defaultProfileImg}
              alt={formatMessage({ id: 'STR_PROFILE_IMAGE_ALT' })}
              screenName={SCREEN_NAMES.PERSONAL_INFO}
            />
            <HeadingText size="lg" level="3" css={{ marginTop: '$half' }}>
              {personalInformation?.displayName}
            </HeadingText>
          </StackLayout>

          <StackLayout orientation="horizontal" horizontalAlignment="center">
            <StackLayout
              css={{
                maxWidth: FORM_CONTENT_MAX_WIDTH,
              }}
            >
              <UserProfileEditableForm
                fields={formFields}
                formState={formState}
                handleSave={handleUserProfileSave}
                isLoading={isUserProfileSaveLoading}
                showAdditionalSettings
                additionalSettingsFooterLink={
                  EXTERNAL_LINKS.ALBERTA_ACCOUNT_SETTINGS
                }
                formSubmission
                pageContext={{
                  productArea: PRODUCT_AREA.SETTINGS,
                  screenName: SCREEN_NAMES.PERSONAL_INFO,
                  subProductArea: SUB_PRODUCT_AREA.PERSONAL_INFO,
                }}
              />
            </StackLayout>
          </StackLayout>
        </>
      )}
    </PageContentWrapper>
  );
};
